import { useEffect, useState } from 'react';
import useNavigationStore from '@src/stores/navigationStore';
import useFilesStore from '@src/stores/filesStore';

import styles from './Identity.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import DropdownList from '@src/components/_shared/UI/DropdownList';
import FileUploadInput from '@src/components/_shared/UI/FileUploadInput';
import ConfirmModal from '@src/components/_shared/ConfirmModal';
import { DocumentLabelType, DocumentType } from '@src/utilities/types';
import toast from 'react-hot-toast';
import { MAXSIZEFILE } from '@src/utilities/constants';
import { useMutation } from '@tanstack/react-query';
import { fileCheck } from '@src/api/Registration';
import useRegistrationStore from '@src/stores/registrationStore';
import InfoBlueBox from '@src/components/_shared/InfoBlueBox';

const filesTypes: Record<number, string> = {
    1: 'Pièce d’identité',
    2: 'Passeport',
    3: 'Carte de résident',
    4: 'Permis de conduire'
};

const documentTypeMapping: Record<string, string> = {
    'Pièce d’identité': 'idcard',
    'Passeport': 'passport',
    'Carte de résident': 'residence',
    'Permis de conduire': 'driving'
};

export default function Identity() {
    const { setSection, setBlock } = useNavigationStore();
    const { registration } = useRegistrationStore();
    const { files, addLegalRepresentativeFile, removeLegalRepresentativeFile } = useFilesStore();

    const [documentType, setDocumentType] = useState<string>('');
    const [keyState, setKeyState] = useState({ documentsListKey: 0, frontKey: 0, backKey: 0 });
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [fileToDelete, setFileToDelete] = useState<number | null>(null);
    const [uploadedDocuments, setUploadedDocuments] = useState<{ front: boolean; back: boolean; }>({ front: false, back: false });
    const [uploadErrors, setUploadErrors] = useState<{ front: string | null; back: string | null; }>({ front: null, back: null });

    const identityFrontFile = files.person.legalRepresentative.find((file) => file.type === DocumentType.front);
    const identityBackFile = files.person.legalRepresentative.find((file) => file.type === DocumentType.back);

    const isPassport = documentType === 'Passeport';

    useEffect(() => {
        if (files.person.legalRepresentative.length > 0 && !documentType) {
            const typeDetail = files.person.legalRepresentative[0].type_detail;
            if (typeDetail) {
                const mappedType = Object.keys(documentTypeMapping).find(
                    (key) => documentTypeMapping[key] === typeDetail
                );
                if (mappedType) {
                    setDocumentType(mappedType);
                }
            } else {
                setDocumentType('');
            }
        }
    }, [files, documentType]);

    useEffect(() => {
        if ((uploadedDocuments.front && uploadedDocuments.back) || (isPassport && uploadedDocuments.front)) {
            setBlock('ProofOfAddress');
        }
    }, [uploadedDocuments, isPassport]);

    const { mutate: mutateFront, isPending: isPendingFront } = useMutation({
        mutationFn: (params: { formData: FormData; isPassport: boolean }) => 
            fileCheck(DocumentType.front, params.formData, params.isPassport),
        onSuccess: () => {
            setUploadedDocuments(prev => ({ ...prev, front: true }));
            setUploadErrors(prev => ({ ...prev, front: null }));
        },
        onError: (error: any) => {
            let errorMessage = `Une erreur est survenue lors de la validation du document.`;
            if (error?.response?.status === 400) {
                errorMessage = 'Le document est invalide. Veuillez en sélectionner un autre.';
            }
            if (error?.response?.status === 409) {
                errorMessage = 'Ce document existe déjà. Veuillez en sélectionner un autre.';
            }
            setUploadErrors(prev => ({ ...prev, front: errorMessage }));
        }
    });

    const { mutate: mutateBack, isPending: isPendingBack } = useMutation({
        mutationFn: (formData: FormData) => fileCheck(DocumentType.back, formData),
        onSuccess: () => {
            setUploadedDocuments(prev => ({ ...prev, back: true }));
            setUploadErrors(prev => ({ ...prev, back: null }));
        },
        onError: (error: any) => {
            let errorMessage = `Une erreur est survenue lors de la validation du document.`;
            if (error?.response?.status === 400) {
                errorMessage = 'Le document est invalide. Veuillez en sélectionner un autre.';
            }
            if (error?.response?.status === 409) {
                errorMessage = 'Ce document existe déjà. Veuillez en sélectionner un autre.';
            }
            setUploadErrors(prev => ({ ...prev, back: errorMessage }));
        }
    });

    const _onGoBack = () => {
        setSection(6);
        setBlock('Documents');
    };

    const _handleSelectFileType = (value: string) => {
        setUploadErrors({ front: null, back: null });
        removeLegalRepresentativeFile(1);
        removeLegalRepresentativeFile(2);
        setDocumentType(filesTypes[Number(value)]);
        setKeyState((prev) => ({ ...prev, documentsListKey: prev.documentsListKey + 1 }));
        setUploadedDocuments({ front: false, back: false });
    };

    const _handleSelectFile = (id: number, file: File, verso: boolean) => {
        const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
        if (!allowedTypes.includes(file.type)) {
            return toast.error('Format de fichier non supporté. Veuillez choisir un fichier PDF ou une image (.png, .jpeg).', { position: 'top-center'});
        }
        if (file.size > MAXSIZEFILE) {
            return toast.error('Ce fichier est trop volumineux. La taille maximum est de 10 Mo.', { position: 'top-center'});
        }
        addLegalRepresentativeFile({
            id: id,
            fileName: file.name,
            fileSize: file.size,
            file: file,
            type: verso ? DocumentType.back : DocumentType.front,
            type_detail: documentTypeMapping[documentType] || '',
            label: file.type === 'application/pdf' ? DocumentLabelType.pdf : DocumentLabelType.image
        });
        if (verso && !isPassport) {
            setUploadErrors(prev => ({ ...prev, back: null }));
            setUploadedDocuments(prev => ({ ...prev, back: false }));
        } else {
            setUploadErrors(prev => ({ ...prev, front: null }));
            setUploadedDocuments(prev => ({ ...prev, front: false }));
        }
    };

    const _handleDeleteFile = (id: number) => {
        setFileToDelete(id);
        setIsModalOpen(true);
    };

    const _confirmDeleteFile = () => {
        if (fileToDelete !== null) {
            removeLegalRepresentativeFile(fileToDelete);
            setKeyState((prev) => ({
                ...prev,
                [fileToDelete === 1 ? 'frontKey' : 'backKey']: prev[fileToDelete === 1 ? 'frontKey' : 'backKey'] + 1,
            }));
            if (fileToDelete === 1) {
                setUploadErrors(prev => ({ ...prev, front: null }));
                setUploadedDocuments(prev => ({ ...prev, front: false }));
            } else {
                setUploadErrors(prev => ({ ...prev, back: null }));
                setUploadedDocuments(prev => ({ ...prev, back: false }));
            }
            if (files.person.legalRepresentative.length <= 1) {
                setDocumentType('');
            }
        }
        setIsModalOpen(false);
        setFileToDelete(null);
        if (files.person.legalRepresentative.length <= 1) {
            setDocumentType('');
            setKeyState((prev) => ({ ...prev, documentsListKey: prev.documentsListKey + 1 }));
        }
    };

    const _cancelDeleteFile = () => {
        setIsModalOpen(false);
        setFileToDelete(null);
    };

    const getFileTypeKey = (documentType: string): string | undefined => {
        if (!documentType) return undefined;
        return Object.keys(filesTypes).find(
            (key) => filesTypes[Number(key)].toLowerCase() === documentType.trim().toLowerCase()
        );
    };

    const createFormData = (file: File, typeDetail: string): FormData => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type_detail', typeDetail);
        formData.append('demand_id', registration.demand_id);
        formData.append('fromWeb', 'true');
        formData.append('registrationType', 'company');
        if (registration.partner_informations.is_partner) {
            formData.append('is_psi', 'true');
            if (registration.person_id) {
                formData.append('person_id', registration.person_id);
            }
            if (registration.partner_informations.brand.slug) {
                formData.append('brand', registration.partner_informations.brand.slug);
            }
        }
        return formData;
    };

    const _onValidate = () => {
        if (!identityFrontFile?.file || !(identityFrontFile.file instanceof File)) {
            return toast.error("Le fichier recto téléchargé est invalide. Veuillez réessayer.");
        }
        if (!isPassport) {
            if (!identityBackFile || !identityBackFile.file || !(identityBackFile.file instanceof File)) {
                return toast.error("Le fichier verso téléchargé est invalide. Veuillez réessayer.");
            }
        }

        const frontFormData = createFormData(
            identityFrontFile.file,
            identityFrontFile.type_detail
        );

        if (!uploadedDocuments.front) {
            mutateFront({ formData: frontFormData, isPassport });
        }

        if (!isPassport && identityBackFile && identityBackFile.file) {
            const backFormData = createFormData(
                identityBackFile.file,
                identityBackFile.type_detail
            );
            if (!uploadedDocuments.back) {
                mutateBack(backFormData);
            }
        }
    };

    return (
        <Container>
            <BackButton onClick={_onGoBack} />
            <BlockTitle title={`Pièce d'identité de ${registration.person.basic.civility} ${registration.person.basic.firstname.charAt(0).toUpperCase() + registration.person.basic.firstname.slice(1)} ${registration.person.basic.lastname.toUpperCase()}, représentant légal`} />
            <Subtitle content="Veuillez charger une pièce d'identité :" />
            <p className={styles.content}>Fichiers acceptés : .pdf, images (.jpeg, .png)</p>
            <div style={{ width: '100%' }}>
                <InfoBlueBox
                    boxTitle="Important"
                    description={isPassport ? "Le numéro de passeport, les noms, prénoms et signature doivent être visibles" : "Merci de charger le recto et le verso séparément."}
                />
                <DropdownList
                    key={`dropdownList-${keyState.documentsListKey}`}
                    title="Type de pièce"
                    width="100%"
                    options={filesTypes}
                    selected={getFileTypeKey(documentType) || 'Choisissez'}
                    onSelect={(value) => _handleSelectFileType(value)}
                />
            </div>
            {documentType ? (
                <FileUploadInput
                    key={`front-${keyState.frontKey}`}
                    title={isPassport ? "Télécharger un document" : "Télécharger un document (recto)"}
                    documentName={identityFrontFile?.fileName}
                    isValidated={!!identityFrontFile?.fileName}
                    selectFile={(file) => _handleSelectFile(1, file, false)}
                    deleteFile={() => _handleDeleteFile(1)}
                    initialFileSize={identityFrontFile?.fileSize}
                    accept=".pdf,image/png,image/jpeg"
                    hasError={!!uploadErrors.front}
                    errorMessage={uploadErrors.front || ''}
                />
            ) : null}

            {!isPassport && (identityFrontFile || identityBackFile) ? (
                <FileUploadInput
                    key={`back-${keyState.backKey}`}
                    title={'Télécharger un document (verso)'}
                    documentName={identityBackFile?.fileName}
                    isValidated={!!identityBackFile?.fileName}
                    selectFile={(file) => _handleSelectFile(2, file, true)}
                    deleteFile={() => _handleDeleteFile(2)}
                    initialFileSize={identityBackFile?.fileSize}
                    accept=".pdf,image/png,image/jpeg"
                    hasError={!!uploadErrors.back}
                    errorMessage={uploadErrors.back || ''}
                />
            ) : null}

            {((isPassport && identityFrontFile) || (identityFrontFile && identityBackFile)) ? (
                <ValidateButton 
                    title="Continuer" 
                    onClick={_onValidate} 
                    disabled={isPendingFront || isPendingBack} 
                    onLoading={isPendingFront || isPendingBack} 
                />
            ) : null}

            <ConfirmModal
                isOpen={isModalOpen}
                onConfirm={_confirmDeleteFile}
                onCancel={_cancelDeleteFile}
            />
        </Container>
    );
}
